/**
 * <ActivationChoiceSection> — une section du composeur qui pose un choix fermé (organism).
 *
 * Trois des neuf sections passent par ici : « Classe à la rentrée », « Heures par semaine » et
 * « Mode de cours ». Contrôlée — `value` + `onChange`, jamais de sélection possédée.
 *
 * **La chrome suit ce que l'option porte** : une option nue est une PILULE de sélection
 * (`<ChoiceChip variant="pill">`, comme `<LateMinutesPicker>`), une option qui porte une phrase
 * de contexte ou une chip devient une CARTE (`<OptionCard>`), parce qu'une pilule n'a pas où les
 * loger. C'est la règle du legacy, où
 * `TabSelect` grandit ses cartes dès qu'une option a un `description` ou un `floatingBadge`.
 *
 * Les options passent à la LIGNE au lieu de défiler : onze classes dans un rail horizontal
 * laissaient « 2nde » hors champ, au point que le legacy a dû écrire un auto-défilement pour
 * ramener la sélection dans la fenêtre. Un retour à la ligne supprime le problème au lieu de le
 * rattraper.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChoiceChip } from "../../atoms/ChoiceChip"
import { type IconName } from "../../atoms/icons/Icon"
import { List } from "../../molecules/List"
import { OptionCard } from "../../molecules/OptionCard"
import { Text } from "../../atoms/typography/Text"
import { type UIActivationChoice } from "../../../ports/primary/ui/types"

/** Largeur plancher d'une carte d'option — « 2h » seul ne fait pas une cible de 44 px de large. */
export const OPTION_MIN_WIDTH = 96

export type ActivationChoiceSectionProps<V extends string | number = string> = {
    title: string
    /** Glyphe de la section — `graduation-cap`, `clock`, `users`. */
    iconName: IconName
    options: UIActivationChoice<V>[]
    /** Valeur retenue — `null` tant que rien n'est choisi. */
    value?: V | null
    onChange: (value: V) => void
    /** Fente de fin d'en-tête — le bouton d'information du mode de cours. */
    headerRight?: React.ReactNode
    /** Phrase sous la rangée — la contrainte du palier sans engagement. */
    note?: string | null
    /** Rangée figée : le choix existe mais il ne se prend pas ici. */
    disabled?: boolean
    testID?: string
}

export function ActivationChoiceSection<V extends string | number = string>(props: ActivationChoiceSectionProps<V>) {
    const { title, iconName, options, value, onChange, headerRight, note, disabled = false, testID } = props

    // Une seule option enrichie suffit à passer toute la rangée en cartes : un alignement de
    // pilules et de cartes se lirait comme deux listes.
    const asCards = options.some((option) => option.description != null || option.badge != null)

    return (
        <List
            title={title}
            titleIcon={iconName}
            headerRight={headerRight}
            testID={testID}
            footer={
                note != null && note !== "" ? (
                    <Text size="text-sm" color="text-muted">
                        {note}
                    </Text>
                ) : undefined
            }
        >
            <View style={styles.row}>
                {options.map((option) =>
                    asCards ? (
                        <View key={String(option.value)} style={styles.option}>
                            <OptionCard
                                label={option.label}
                                description={option.description ?? undefined}
                                badge={option.badge ?? undefined}
                                badgeVariant="accent"
                                selected={option.value === value}
                                disabled={disabled}
                                onPress={() => onChange(option.value)}
                            />
                        </View>
                    ) : (
                        <ChoiceChip
                            key={String(option.value)}
                            variant="pill"
                            label={option.label}
                            selected={option.value === value}
                            disabled={disabled}
                            onPress={() => onChange(option.value)}
                        />
                    )
                )}
            </View>
        </List>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
    },
    option: { minWidth: OPTION_MIN_WIDTH },
}))
